import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Label,
  TextField,
  Select,
  Radio,
  RadioItem,
  MenuItem,
  Icon,
  Button,
  Tooltip,
  Banner,
} from 'mw-style-react';
import AppUtils from '@control-front-end/utils/utils';
import {
  GET_ACCOUNT_CURRENCIES,
  GET_ACCOUNT_NAMES,
} from '@control-front-end/common/constants/actorAccounts';
import AccAdvancedOptions from '@control-front-end/common/components/AccAdvancedOptions';
import Toggle from '@control-front-end/common/components/Toggle';
import useIntl from 'useIntl';
import mes from './intl';
import './FormAccounts.scss';

function FormAccounts({ formAccounts, handleChangeAccounts }) {
  const t = useIntl();
  const dispatch = useDispatch();
  const [currencies, setCurrencies] = useState([]);
  const [accNames, setAccNames] = useState([]);

  useEffect(() => {
    dispatch({
      type: GET_ACCOUNT_CURRENCIES.REQUEST,
      callback: (data) => setCurrencies(data),
    });
    dispatch({
      type: GET_ACCOUNT_NAMES.REQUEST,
      callback: (data) => setAccNames(data),
    });
  }, []);

  /**
   * Добавление нового счета формы
   */
  const handleAddItem = () => {
    const newFormAccounts = formAccounts.slice();
    newFormAccounts.push({
      id: `new-${AppUtils.createRid()}`,
      action: 'create',
      accountType: 'fact',
      nameAction: 'select',
      curAction: 'select',
      nameId: null,
      currencyId: null,
      treeCalculation: true,
      search: true,
    });
    handleChangeAccounts(newFormAccounts);
  };

  /**
   * Редактирование счета формы
   */
  const handleChangeItem = ({ id, param }) => {
    const newFormAccounts = formAccounts.slice();
    const itemIndex = newFormAccounts.findIndex((item) => item.id === id);
    if (itemIndex !== -1) {
      const updatedItem = newFormAccounts[itemIndex];
      updatedItem[param.id] = param.value;
      newFormAccounts.splice(itemIndex, 1, updatedItem);
      handleChangeAccounts(newFormAccounts);
    }
  };

  /**
   * Удаление счета формы
   */
  const handleDeleteItem = (id) => {
    const newFormAccounts = formAccounts.slice();
    const itemIndex = newFormAccounts.findIndex((item) => item.id === id);
    if (itemIndex !== -1) {
      const deletedItem = newFormAccounts[itemIndex];
      const isNew = id.toString().indexOf('new-') === 0;
      if (isNew) {
        newFormAccounts.splice(itemIndex, 1);
      } else {
        deletedItem.action = 'delete';
        newFormAccounts.splice(itemIndex, 1, deletedItem);
      }
      handleChangeAccounts(newFormAccounts);
    }
  };

  const renderAccNames = () => {
    const items = accNames.map((i) => (
      <MenuItem key={i.id.toString()} label={i.name} value={i.id.toString()} />
    ));
    if (!items.length) {
      return <MenuItem label={t(mes.formAccNotFound)} />;
    }
    return items;
  };

  const renderCurrencies = (nameId, nameAction) => {
    const list = nameAction === 'select' && !nameId ? [] : currencies;
    const excluded =
      nameAction === 'select'
        ? formAccounts
            .filter(
              (acc) => acc.nameId && acc.currencyId && acc.nameId === nameId
            )
            .map((i) => i.currencyId.toString())
        : [];
    const items = list.map((i) => (
      <MenuItem
        key={i.id.toString()}
        label={i.name}
        value={i.id.toString()}
        visibility={excluded.includes(i.id.toString()) ? 'disabled' : 'visible'}
      />
    ));
    if (!items.length) {
      return <MenuItem label={t(mes.formAccNotFound)} />;
    }
    return items;
  };

  const renderAccountRow = (formAcc) => {
    const {
      id,
      action,
      currencyId,
      nameId,
      accName,
      currencyName,
      curAction,
      nameAction,
      minLimit,
      maxLimit,
      treeCalculation,
      search,
    } = formAcc;
    const isNew = id.toString().indexOf('new-') === 0;
    const inputVisibility = isNew ? 'visible' : 'disabled';
    if (action === 'delete') return null;
    return (
      <div key={id} styleName="fa__section">
        <div styleName="fa__section__col">
          <div styleName="fa__section__row">
            <div styleName="fa__section__col">
              {isNew ? (
                <Label
                  styleName="fa__section__title"
                  value={t(mes.formAccSelectOrCreateName)}
                />
              ) : null}
              <Radio
                align="horizontal"
                value={nameAction}
                onChange={({ value }) =>
                  handleChangeItem({ id, param: { id: 'nameAction', value } })
                }
                visibility={isNew ? 'visible' : 'hidden'}
              >
                <RadioItem label={t(mes.formAccSelectName)} value="select" />
                <RadioItem label={t(mes.formAccСreateName)} value="create" />
              </Radio>
              {nameAction === 'create' ? (
                <TextField
                  styleName="field"
                  label={t(mes.formAccEnterName)}
                  bordered={true}
                  size="large"
                  value={accName || ''}
                  onChange={({ value }) =>
                    handleChangeItem({ id, param: { id: 'accName', value } })
                  }
                />
              ) : (
                <Select
                  styleName="field"
                  size="large"
                  label={
                    isNew ? t(mes.formAccSelectName) : t(mes.formAccEnterName)
                  }
                  bordered={true}
                  type="autocomplete"
                  value={nameId}
                  visibility={inputVisibility}
                  onChange={({ value }) =>
                    handleChangeItem({ id, param: { id: 'nameId', value } })
                  }
                >
                  {renderAccNames()}
                </Select>
              )}
            </div>
            <div styleName="fa__section__col">
              {isNew ? (
                <Label
                  styleName="fa__section__title"
                  value={t(mes.formAccSelectOrCreateCurrency)}
                />
              ) : null}
              <Radio
                align="horizontal"
                value={curAction}
                visibility={isNew ? 'visible' : 'hidden'}
                onChange={({ value }) =>
                  handleChangeItem({ id, param: { id: 'curAction', value } })
                }
              >
                <RadioItem
                  label={t(mes.formAccSelectCurrency)}
                  value="select"
                />
                <RadioItem
                  label={t(mes.formAccCreateCurrency)}
                  value="create"
                />
              </Radio>
              {curAction === 'create' ? (
                <TextField
                  styleName="field"
                  label={t(mes.formAccEnterCurrencyName)}
                  bordered={true}
                  size="large"
                  value={currencyName || ''}
                  onChange={({ value }) =>
                    handleChangeItem({
                      id,
                      param: { id: 'currencyName', value },
                    })
                  }
                />
              ) : (
                <Select
                  styleName="field"
                  size="large"
                  label={
                    isNew
                      ? t(mes.formAccSelectCurrency)
                      : t(mes.formAccEnterCurrencyName)
                  }
                  bordered={true}
                  type="autocomplete"
                  value={currencyId ? currencyId.toString() : currencyId}
                  onChange={({ value }) =>
                    handleChangeItem({ id, param: { id: 'currencyId', value } })
                  }
                  visibility={
                    nameAction === 'select' && !nameId
                      ? 'disabled'
                      : inputVisibility
                  }
                >
                  {renderCurrencies(nameId, nameAction)}
                </Select>
              )}
            </div>
          </div>
          <Toggle title={t(mes.advancedOptions)} theme="white" isOpen={false}>
            <AccAdvancedOptions
              values={{ minLimit, maxLimit, treeCalculation, search }}
              errors={{}}
              mode="create"
              disabled={!isNew}
              addPlan={false}
              handleOnChange={(param) => {
                handleChangeItem({ id, param });
              }}
            />
          </Toggle>
        </div>
        <div>
          <Tooltip styleName="fa__tooltip" value={t(mes.removeFormAcc)}>
            <Icon
              styleName="fa__section__close"
              type="close"
              onClick={() => handleDeleteItem(id)}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <div styleName="fa">
      <Banner
        styleName="fa__disclaimer"
        size="medium"
        value={t(mes.formAccDisclaimer)}
      />
      {formAccounts.map((acc) => renderAccountRow(acc))}
      <Button
        type="text"
        fontWeight="normal"
        label={t(mes.addFormAcc)}
        onClick={handleAddItem}
      />
    </div>
  );
}

FormAccounts.propTypes = {
  formAccounts: PropTypes.array,
  handleChangeAccounts: PropTypes.func,
};

export default FormAccounts;
